import { Injectable } from '@angular/core';
import { ActionCompletion, Actions, ofActionCompleted, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { has } from 'lodash';
import {
  AuthSessionLogin,
  AuthLogin,
  AuthLogout,
  AuthRedirectUserToDashboard,
  AuthSetInitialState,
  AuthSetUser,
  AuthResetPassword,
  AuthSSOLogin,
  AuthRedirectUserToChangePassword,
  AuthLogoutWithSkid,
  AuthInit,
} from './auth.actions';
import { AuthState } from './auth.state';
import { LoginModel } from './models/login.model';
import { PermissionModel, SetInitialStateFacade } from '../../../common/models';
import { AuthenticationIdpType, AuthMeResponse, AuthUserData, UserDataRole } from './models';
import { UserOwnerType } from '../administration/user/constants';
import { Language, Role } from '../../../common/constants';
import { Permission } from './../../../common/constants/permissions';
import { transformPermissionArrayToObject } from '../../../common/utils';

@Injectable({ providedIn: 'root' })
export class AuthFacade implements SetInitialStateFacade {
  userData$: Observable<AuthUserData> = this.store.select(AuthState.getUserData);
  permissions$: Observable<PermissionModel[]> = this.store.select(AuthState.getPermissions);
  roles$: Observable<UserDataRole[]> = this.store.select(AuthState.getRoles);
  userId$: Observable<number> = this.store.select(AuthState.getUserId);
  ownerType$: Observable<UserOwnerType> = this.store.select(AuthState.getOwnerType);
  defaultLanguage$: Observable<Language> = this.store.select(AuthState.getDefaultLanguage);
  country$: Observable<string> = this.store.select(AuthState.getCountry);
  timezone$: Observable<string> = this.store.select(AuthState.getTimezone);
  isTwentyFourHourTime$: Observable<boolean> = this.store.select(AuthState.getIsTwentyFourHourTime);
  renderTimezone$: Observable<string> = this.store.select(AuthState.getRenderTimezone);
  isInternalUser$: Observable<boolean> = this.store.select(AuthState.isInternalUser);
  authenticationIdp$: Observable<AuthenticationIdpType> = this.store.select(AuthState.getAuthenticationIdp);
  constructor(private store: Store, private actions: Actions) {}

  login(userCredentials: LoginModel): void {
    this.store.dispatch(new AuthLogin(userCredentials));
  }

  ssoLogin(token: string): void {
    this.store.dispatch(new AuthSSOLogin(token));
  }

  sessionLogin(hash: string): void {
    this.store.dispatch(new AuthSessionLogin(hash));
  }

  logout(redirectUrl?: string): void {
    this.store.dispatch(new AuthLogout(redirectUrl));
  }

  logoutWithSkid(): void {
    this.store.dispatch(new AuthLogoutWithSkid());
  }

  setUser(data: AuthUserData): void {
    this.store.dispatch(new AuthSetUser(data));
  }

  setUserFromMe(me: AuthMeResponse): void {
    this.store.dispatch(new AuthSetUser(me));
  }

  redirectUserToDashboard(): boolean {
    this.store.dispatch(new AuthRedirectUserToDashboard());
    return false;
  }

  getSkidUrl(): string {
    return this.store.selectSnapshot(AuthState.getSkidUrl);
  }

  redirectToChangePassword(): void {
    this.store.dispatch(new AuthRedirectUserToChangePassword());
  }

  resetPassword(email: string): Observable<ActionCompletion<AuthResetPassword, Error>> {
    this.store.dispatch(new AuthResetPassword(email));
    return this.actions.pipe(
      ofActionCompleted(AuthResetPassword),
      take(1),
      filter(({ result }: ActionCompletion<AuthResetPassword, Error>) => result.successful),
    );
  }

  hasPermission$(requiredPermission: string): Observable<boolean> {
    return this.permissions$.pipe(
      filter(Boolean),
      take(1),
      map(
        (userPermissions: PermissionModel[]) =>
          has(transformPermissionArrayToObject(userPermissions), requiredPermission) ||
          requiredPermission === Permission.AllUsers,
      ),
    );
  }

  hasRole$(requiredRole: Role): Observable<boolean> {
    return this.roles$.pipe(
      filter(Boolean),
      take(1),
      map((userRoles: UserDataRole[]) => userRoles.some((data: UserDataRole) => data.role === requiredRole)),
    );
  }

  setInitialState(): void {
    this.store.dispatch(new AuthSetInitialState());
  }

  bootstrapFromSession(): void {
    this.store.dispatch(new AuthInit());
  }
}
